import { useMutation } from "@apollo/client";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ExtractionReviewValues } from "components/Document/ExtractionReview";
import { useCallback } from "react";

import { SHIPPING_PAPERWORK_QUERY } from "./api";
import {
  APPLY_EXTRACTED_BILL_OF_LADING,
  EXTRACTED_BILL_OF_LADING,
  SHIPPING_DOCUMENT_FILE,
} from "./billOfLadingApi";
import { lineNumberBlockers, toApplyInput } from "./extractionReview";
import { PROCESSED_MANIFEST_SHOW_FILTER_COUNTS_QUERY } from "./useShowFilterCounts";

// The reason goes in the message itself: the alert's second argument is an
// error object whose text sits behind the copy control.
function notSavedMessage(blockers: readonly string[]): string {
  const reasons = blockers.map((blocker) => blocker.replace(/\.$/, ""));
  return `The bill of lading was not saved. ${reasons.join(". ")}.`;
}

const REFETCH_AFTER_APPLY = [
  EXTRACTED_BILL_OF_LADING,
  SHIPPING_PAPERWORK_QUERY,
  PROCESSED_MANIFEST_SHOW_FILTER_COUNTS_QUERY,
  SHIPPING_DOCUMENT_FILE,
];

/**
 * Blockers come back as data and nothing was written. Refetching the reading
 * then would hand the form the other reviewer's newer version.
 */
export function queriesToRefetchAfterApply(result: {
  data?: { applyExtractedBillOfLading?: { blockers: readonly string[] } | null } | null;
}) {
  return result.data?.applyExtractedBillOfLading?.blockers.length ? [] : REFETCH_AFTER_APPLY;
}

/**
 * Applying a reviewed bill of lading reading.
 *
 * Line container counts and quantities are checked before the mutation. After
 * that the apply either returns the manifest id, returns blockers (nothing was
 * written), or surfaces the server's rejection.
 *
 * After a write, refetches the reading (its `candidateStatus` flips to APPLIED),
 * the merged shipping-paperwork list, and the counts behind the needs-review
 * prompt. Blockers refetch nothing.
 */
export function useApplyReviewedBillOfLading({
  documentId,
  facilityId,
  isConfirmed = false,
  savedWasteManifestId = null,
  savedWasteManifestVersion = null,
  onApplied,
}: {
  documentId: string;
  facilityId: string;
  /** A reviewer already confirmed this bill, so this apply edits it. */
  isConfirmed?: boolean;
  /** The saved manifest the form was loaded from; the server refuses a stale save. */
  savedWasteManifestId?: string | null;
  /** That manifest's version when the form was loaded; a later save of it makes this save stale. */
  savedWasteManifestVersion?: string | null;
  onApplied: (wasteManifestId: string) => void;
}) {
  const alerts = useAlerts();
  const [applyMutation, { loading: applying }] = useMutation(APPLY_EXTRACTED_BILL_OF_LADING, {
    refetchQueries: queriesToRefetchAfterApply,
  });

  const confirm = useCallback(
    async (reviewed: ExtractionReviewValues) => {
      const lineBlockers = lineNumberBlockers(reviewed);
      if (lineBlockers.length > 0) {
        alerts.error(notSavedMessage(lineBlockers));
        return;
      }
      try {
        const { data } = await applyMutation({
          variables: {
            input: toApplyInput(reviewed, {
              documentId,
              facilityId,
              savedWasteManifestId,
              savedWasteManifestVersion,
            }),
          },
        });
        const result = data?.applyExtractedBillOfLading;
        if (result?.blockers.length) {
          alerts.error(notSavedMessage(result.blockers));
          return;
        }
        if (!result?.wasteManifestId) {
          throw new Error("The apply did not return a saved bill of lading.");
        }
        alerts.success(isConfirmed ? "Bill of lading saved." : "Bill of lading applied.");
        onApplied(result.wasteManifestId);
      } catch (error) {
        alerts.error(
          "Could not apply the bill of lading",
          error instanceof Error ? error.message : undefined
        );
      }
    },
    [
      alerts,
      applyMutation,
      documentId,
      facilityId,
      isConfirmed,
      onApplied,
      savedWasteManifestId,
      savedWasteManifestVersion,
    ]
  );

  return { confirm, applying };
}
